// ============================================================================
// CENTRAL MODEL FILE - All Interfaces and Types for the Project
// ============================================================================

// ============================================================================
// 1. SHARED/COMMON INTERFACES
// ============================================================================

export interface HTMLInputEvent extends Event {
  target: HTMLInputElement & EventTarget;
}

export interface TableAction {
  icon?: string; // SVG string or icon name
  iconPath?: string; // Path to icon file (SVG or PNG)
  action: string;
  title: string;
  type?: 'button' | 'checkbox';
  checkedField?: string;
  showText?: boolean; // Show text next to icon
  buttonColor?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger' | 'default'; // Button background color
  iconSize?: 'small' | 'medium' | 'large'; // Icon size
  condition?: (row: any) => boolean; // Condition function to determine if action should be shown
  preventToggleWhen?: (row: any) => boolean; // Prevent checkbox state changes but still emit the action
}

export interface TableColumn {
  field: string;
  header: string;
  type?: 'text' | 'link' | 'badge' | 'action' | 'date' | 'number' | 'linked-tender' | 'radio' | 'custom';
  linkRoute?: string;
  badgeClass?: string;
  badgeColor?: BadgeColor | ((row: any) => BadgeColor);
  badgeSize?: 'small' | 'medium' | 'large';
  format?: string;
  actionIcon?: string;
  actionTitle?: string;
  actions?: TableAction[];
  sortable?: boolean;
  showCurrentTag?: boolean;
  filterable?: boolean;
  // Radio button specific
  radioGroupName?: string;
  isSelected?: (row: any) => boolean;
  onSelect?: (row: any, event: Event) => void;
  // Custom rendering
  customTemplate?: (row: any) => string;
}

// Filter Types
export type TextFilterOperator = 'equals' | 'doesNotEqual' | 'contains' | 'doesNotContain' | 'startsWith' | 'endsWith';
export type NumberFilterOperator = 'equals' | 'doesNotEqual' | 'greaterThan' | 'lessThan' | 'between';
export type DateFilterOperator = 'equals' | 'doesNotEqual' | 'before' | 'after' | 'between' | 'today' | 'thisWeek' | 'thisMonth';
export type SelectFilterOperator = 'isOneOf' | 'isNotOneOf';
export type FilterOperator = TextFilterOperator | NumberFilterOperator | DateFilterOperator | SelectFilterOperator;

export interface ColumnFilter {
  field: string;
  operator: FilterOperator;
  values: any[];
  sortDirection?: 'asc' | 'desc' | null;
}

export type SortDirection = 'asc' | 'desc' | null;

export interface SortOption {
  label: string;
  direction: SortDirection;
  icon: string;
}

export type BadgeColor = 'green' | 'blue' | 'pink' | 'gray' | 'yellow' | 'red' | 'orange' | 'purple';

// ============================================================================
// 2. DASHBOARD MODELS
// ============================================================================

export interface SummaryCard {
  title: string;
  value: number;
  icon: string;
  iconColor: string;
}

export interface PieChartData {
  labels: string[];
  datasets: {
    data: number[];
    backgroundColor: string[];
    borderColor?: string[];
    borderWidth?: number;
  }[];
}

export interface LineChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    borderColor: string;
    backgroundColor: string;
    tension: number;
    fill: boolean;
    borderWidth?: number;
    pointRadius?: number;
    pointHoverRadius?: number;
  }[];
}

export interface BarChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor?: string;
    borderWidth?: number;
    borderRadius?: number;
  }[];
}

export interface PendingVote {
  tenderId: string;
  title: string;
  value: number;
  deadline: Date;
  status: string;
}

export interface DashboardData {
  summaryCards: SummaryCard[];
  tenderStatusChart: PieChartData;
  evaluationProgressChart: PieChartData;
  monthlyTrendChart: LineChartData;
  departmentTendersChart: BarChartData;
  budgetChart: BarChartData;
  pendingVotes: PendingVote[];
}

// ============================================================================
// 3. MEETINGS & MINUTES OF MEETING
// ============================================================================

export interface BasicInfo {
  subject: string;
  meetingId: string;
  department: string;
  createdDate: string;
  meetingDate: string;
  meetingTime: string;
  location: string;
}

export interface AgendaInfo {
  titleEnglish: string;
  titleArabic: string;
}

export interface Attendee {
  name: string;
}

export interface QuickInfo {
  meetingDate: string;
  department: string;
  currentStage: string;
}

export interface AttachmentInfo {
  fileName: string;
  fileSize: string;
  url?: string;
}

export interface AttendanceTableMember {
  number: number;
  memberNameArabic: string;
  memberNameEnglish: string;
  roleArabic: string;
  roleEnglish: string;
  attendance: boolean;
}

export interface AgendaDescription {
  english: string;
  arabic: string;
}

export interface MeetingItem {
  agendaId: number;
  itemNumber: string;
  company: string;
  subject?: string;
  englishText: string;
  arabicText: string;
  decisionEnglish: string;
  decisionArabic: string;
  acceptedVotes: number;
  rejectedVotes: number;
  deferredVotes?: number;
  totalVotes?: number;
}

export interface MeetingMinutesData {
  recordedBy: string;
  recordedDate: string;
  summary: string;
  attendanceTable: AttendanceTableMember[];
  agendaDescription: AgendaDescription;
  items: MeetingItem[];
}

export interface MinutesOfMeetingDetails {
  basicInfo: BasicInfo;
  agendaInfo: AgendaInfo;
  attendees: Attendee[];
  discussionPoints: string;
  decisionsMade: string;
  actionItems: string;
  quickInfo: QuickInfo;
  attachment: AttachmentInfo;
  meetingMinutes: MeetingMinutesData;
}

export interface MinutesOfMeetingData {
  subject: string;
  meetingId: string;
  agendaTitleEN: string;
  agendaTitleAR: string;
  currentStage: string;
  department: string;
  requiredFromTC: string;
  hasAttachment: boolean;
}

// From start-meeting-dialog
export interface AgendaItem {
  subject: string;
  dateTime: string;
  descriptionEnglish: string;
  descriptionArabic: string;
  tcNotes: string;
  requiredFromTC: string;
  department?: string;
  response?: string;
  attachmentsCount?: number;
  attachments?: Attachment[];
  currentStage?: string;
  tcDecisionAR?: string;
  tcDecisionEN?: string;
  prIdentifiers?: string[];
}

// From view-meeting
export interface Attachment {
  name: string;
  size: string;
  uploadedBy?: string;
  uploadedDate?: string;
  url?: string;
}

// Meeting Attachment with extended properties
export interface MeetingAttachment {
  name: string;
  size: string;
  uploadedBy?: string;
  uploadedDate?: string;
  url?: string;
  description?: string;
  date?: Date;
  attachmentFor?: string;
  fileContent?: string;
  additionalDetails?: string;
  mimeType?: string;
}

export interface Member {
  name: string;
  role: string;
  initials: string;
  status: string;
}

export interface MeetingDetails {
  meetingId: string;
  title: string;
  dateTime: string;
  duration: string;
  location: string;
  status: string;
  createdBy: string;
  createdDate: string;
  members: Member[];
  agendaItems: AgendaItem[];
  meetingMinutes: any;
  meetingMinutesData: MeetingMinutesData;
}

// ============================================================================
// 4. TENDERS
// ============================================================================

// Tender interfaces will be added here if needed
// Currently using TenderModel from authentication models

// ============================================================================
// 5. BIDS
// ============================================================================

export interface BidAttachment {
  category?: string;
  name: string;
  size: number;
  sizeFormatted: string;
  status: string;
  date?: Date | string;
  fileUrl: string;
  mimeType: string;
  attachmentId?: string;
  isUnsealed?: boolean;
  documentExpiryDate?: Date | string;
}

export interface BidAttachmentGroup {
  category: string;
  attachments: BidAttachment[];
}

export interface BidDetails {
  bidId: string;
  tenderId: string;
  vendor: string;
  vendorId?: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  submissionDate: Date | string;
  bidValue: number;
  deliveryPeriod: string;
  paymentTerms: string;
  warrantyPeriod: string;
  status: string;
  tcCommitteeStatus?: string;
  awardDecision: string;
  evaluationType: string;
  isBidLocked?: boolean;
  isBidDecisionFinal?: boolean;
  sealedBidMessage?: string;
  joiningFee?: number | null;
  paymentReferenceNumber?: string;
  accountNumber?: string;
  bankName?: string;
  paymentDate?: Date | string | null;
  joiningFeeDocuments?: any[];
}

export interface BidEvaluationResult {
  evaluationId: string;
  evaluationType: string;
  evaluationStatus: string;
  decision: string;
  comments: string;
  rejectionReason: string;
  decisionDate?: Date | string | null;
}

export interface BidInfo {
  bidId: string;
  vendorName: string;
  amount: number;
  submissionDate: string;
  status: string;
  generalRemarks?: string;
  actionTaken?: string; // ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¹ÃƒÆ’Ã¢â‚¬Â¦ÃƒÂ¢Ã¢â€šÂ¬Ã…â€œÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â§ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¾ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¾ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¹ÃƒÆ’Ã¢â‚¬Â¦ÃƒÂ¢Ã¢â€šÂ¬Ã…â€œÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¥ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¹ÃƒÆ’Ã¢â‚¬Â¦ÃƒÂ¢Ã¢â€šÂ¬Ã…â€œÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¹ÃƒÆ’Ã¢â‚¬Â¦ÃƒÂ¢Ã¢â€šÂ¬Ã…â€œÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â±ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¹ÃƒÆ’Ã¢â‚¬Â¦ÃƒÂ¢Ã¢â€šÂ¬Ã…â€œÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â§ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¹ÃƒÆ’Ã¢â‚¬Â¦ÃƒÂ¢Ã¢â€šÂ¬Ã…â€œÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¡ ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¹ÃƒÆ’Ã¢â‚¬Â¦ÃƒÂ¢Ã¢â€šÂ¬Ã…â€œÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â§ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¾ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¾ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¾ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¦ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¹ÃƒÆ’Ã¢â‚¬Â¦ÃƒÂ¢Ã¢â€šÂ¬Ã…â€œÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚ÂªÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¹ÃƒÆ’Ã¢â‚¬Â¦ÃƒÂ¢Ã¢â€šÂ¬Ã…â€œÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â®ÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã¢â‚¬Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¹ÃƒÆ’Ã¢â‚¬Â¦ÃƒÂ¢Ã¢â€šÂ¬Ã…â€œÃƒÆ’Ã†â€™Ãƒâ€ Ã¢â‚¬â„¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã‚Â¡ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â°: 'Approved', 'Rejected', etc.
  isCurrent?: boolean;
}

export interface BidEvaluationInfo {
  id: number;
  vendorId?: string;
  bidId: string;
  decision: 'approved' | 'rejected' | null;
  comments: string;
  reviewComments?: string;
  vendorName: string;
  submissionDate: string;
  rejectionReason: string;
  canChangeDecision?: boolean;
}

// API Response Models for GetAllBids
export interface BidItem {
  bidId: string;
  vendorId?: string;
  tenderId: string;
  vendorName: string;
  submissionDeadline: string;
  status: string;
  tcComitteeStatus?: string;
  evaluationType: string;
  totalScore: number;
  votingStatus: string;
  decision: string;
  isBidLocked?: boolean;
  isBidDecisionFinal?: boolean;
  sealedBidMessage?: string;
}

export interface GetAllBidsResponse {
  data: BidItem[];
  isSuccess: boolean;
  errorMessageEn: string;
  errorMessageAr: string;
  code: number;
  totalCount: number;
  message: string | null;
  result: any | null;
}

// ============================================================================
// 6. EVALUATIONS
// ============================================================================

export interface Evaluation {
  id: number;
  evaluationId: string;
  prNumber: string; // PR number this evaluation is linked to
  tenderId: string; // Kept for navigation to tender details
  evaluationType: string;
  evaluatorId: number;
  assignedToEn: string;
  assignedToAr: string;
  status: string;
  score: number | null;
  comments: string | null;
  tender: {
    tenderID: string;
    typeofTender: string;
    managedBy: string | null;
  } | null;
  evaluator: {
    evaluatorId: string;
    evaluatorNameEN: string;
  } | null;
  // Optional properties used in component methods
  bidId?: string;
  criteria?: any[];
  selectedEvaluatorIds?: string[];
  dueDate?: string | null;
  assignedEvaluators?: AssignedEvaluator[];
}

export interface EvaluationDetails {
  evaluationId: string;
  tenderId: string;
  prNumber: string;
  tenderTitle: string;
  evaluationType: string;
  evaluatorId: string;
  status: string;
  comments: string;
  recommendation: string;
  score: number;
  allBids: BidInfo[];
  reviewComments: string;
  assignmentInfo: AssignmentInfo;
  timelineEvents: TimelineEvent[];
}


export interface AssignmentInfo {
  assignedToEn: string;
  assignedToAr: string;
  assignedDate: string;
  submittedDate: string;
  assignedEvaluators?: AssignedEvaluator[];
}

export interface AssignedEvaluator {
  userId: number;
  fullName: string;
  nameAr: string;
  email: string;
}

export interface TimelineEvent {
  title: string;
  date: string;
  type: 'submitted' | 'started' | 'assigned' | 'accepted';
}

export interface EvaluationData {
  tenderId?: string;
  evaluationType?: string;
  evaluationId?: string;
  prNumber?: string;
  status?: string;
  reviewComments?: string;
}

export interface Evaluator {
  id: string;
  name: string;
  role: string;
}


export interface Document {
  name: string;
  attachmentId?: string;
  attachmentFor?: string;
  uploadedDate?: string;
  expiryDate?: string;
  fileContent?: string;
  mimeType?: string;
  isUnsealed?: boolean;
  size: string;
  filePath?: string;
}

// ============================================================================
// 7. PURCHASE REQUISITIONS (PRs)
// ============================================================================

export interface PRDocument {
  name: string;
  size: number; // in bytes
  sizeFormatted: string; // e.g., "2.4 MB"
  date: Date | string;
  attachmentId?: string;
  mimeType?: string;
  fileContent?: string;
  filePath?: string;
}

export interface PRDetails {
  prNumber: string;
  prName?: string;
  department: string;
  requisitionPurpose: string;
  value: number;
  isTC?: boolean;
  approvedDate: Date | string;
  requestor: string;
  requestDate: Date | string;
  transactionDate?: Date | string;
  submittedBy?: string;
  description: string;
  justification: string;
  specifications: string[];
  status: string;
  linkedTender?: string;
  documents: PRDocument[];
  lines: PRLines[];
}

export interface PRLines {
  prLineNumber: string;
  prNumber: string;
  productName: number;
  quantity: number;
}
// ============================================================================
// 8. AUTHENTICATION & VENDOR MODELS
// ============================================================================

// Note: These are classes, not interfaces, so they are kept in their original file
// but referenced here for documentation purposes
// See: QNBN - TCA Portal FE/src/app/modules/authentication/components/models/autentication-dto.models.ts
// 
// Classes included:
// - ValidateUserDTO
// - ConfirmEmail
// - ChangePassword
// - DocumentModel
// - DocumentsMaster
// - CategoryModel
// - ManagementTeamModel
// - AssociatedCompanyModel
// - SubCategoryModel
// - BankAccountModel
// - VendorModel
// - IdentificationList
// - ContactPersonModel
// - BidModel
// - TenderModel
// - VendorTenderParticipation
// - Attachment
// - evaluationForm
// - VendorDto
// - BaseEntity
// - DocumenDto
// - Category
// - ContactPerson
// - ManagementTeam
// - AssociatedCompany
// - SubCategory
// - BankAccount
// - TheDocuments
// - LoginOTPDTO

// ============================================================================
// 9. COMMON UTILITY MODELS
// ============================================================================

export class LabelValue {
  labelEn: string;
  labelAr: string;
  value: number;
  cssClass: string;
  descEn: string;
  descAr: string;
  constructor({labelEn, labelAr, value, cssClass, descEn, descAr}: {labelEn?: any, labelAr?: any, value?: any, cssClass?: any, descEn?: any, descAr?: any} = {}) { 
    this.labelEn = labelEn;
    this.labelAr = labelAr;
    this.value = value;
    this.cssClass = cssClass;
    this.descAr = descAr;
    this.descEn = descEn;
  }
}

export class CountryMaster {
  countryName: string = '';     
  countryRegionId: string = '';  
}

export class Message {
  direction?: string;
  moudle?: string; // module, like general or bidding
  vendorID?: string;
  subReferenceNumber?: number;  // invoice number
  referenceNumber?: string | null; // e.g., bidding id, PO, null for general messages
  messageContent?: string;
  CreatedAt? : string | null;
}

export class CurrencyMaster {
  description: string = '';     
  currencyId: string = '';  
}

export class User {
    Id: string;
    FullName: string;
    MobilePhone: string;
    EmailAddress: string;
    BirthDate: string;
    Gender: string;
    QID: string;
    Status: string;
    Active: string;
    Nationality: string;
    Role?: string;

    constructor({Id, FullName, MobilePhone, EmailAddress, BirthDate, Gender, QID, Status, Active, Nationality, Role}: 
                {Id?: any, FullName?: any, MobilePhone?: any, EmailAddress?: any, BirthDate?: any, Gender?: any, QID?: any, Status?: any, Active?: any, Nationality?: any, Role?: any}= {}) {
        this.Id = Id;
        this.FullName = FullName;
        this.MobilePhone = MobilePhone,
        this.EmailAddress = EmailAddress,
        this.BirthDate = BirthDate,
        this.Gender = Gender,
        this.QID = QID;
        this.Status = Status;
        this.Active = Active;
        this.Nationality = Nationality;
        this.Role = Role;
    }
}

export class RequestsDTO {
  items: Array<any> = [];
  totalCount: number = 0;
}

export class ErrorDto {
  inputId: string = '';
  message: string = '';
}
